// SealCelebration: Modal shown when a technique is mastered (Chaos Source sealed)
import { useEffect } from "react";
import { X } from "lucide-react";
import { Button } from "@/components/ui/button";

interface SealCelebrationProps {
  show: boolean;
  techniqueName: string;
  xpEarned?: number;
  onClose: () => void;
}

export function SealCelebration({
  show,
  techniqueName,
  xpEarned = 0,
  onClose,
}: SealCelebrationProps) {
  // Auto-dismiss after 5 seconds
  useEffect(() => {
    if (show) {
      const timer = setTimeout(() => {
        onClose();
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [show, onClose]);

  if (!show) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 animate-fade-in">
      <div className="bg-white dark:bg-gray-800 rounded-lg p-8 max-w-md w-full mx-4 text-center transform animate-seal-reveal shadow-2xl border-4 border-yellow-400">
        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 p-1 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
          aria-label="Close"
        >
          <X className="w-5 h-5 text-gray-500" />
        </button>

        {/* Icon */}
        <div className="text-8xl mb-4 animate-bounce">🔒</div>

        {/* Heading */}
        <h2 className="text-4xl font-bold text-gray-900 dark:text-white mb-2 uppercase tracking-tight">
          Chaos Source
          <br />
          <span className="text-yellow-600 dark:text-yellow-400">Sealed!</span>
        </h2>

        {/* Technique Name */}
        <div className="my-6 p-4 bg-gradient-to-r from-blue-50 to-purple-50 dark:from-blue-900/30 dark:to-purple-900/30 rounded-lg border-2 border-blue-300 dark:border-blue-700">
          <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">
            Ancient Form Mastered
          </p>
          <p className="text-2xl font-bold text-gray-900 dark:text-white">
            {techniqueName}
          </p>
        </div>

        {/* XP Badge */}
        {xpEarned > 0 && (
          <div className="mb-6 flex items-center justify-center gap-2">
            <span className="text-3xl">✨</span>
            <div className="px-4 py-2 bg-green-100 dark:bg-green-900/30 rounded-lg border-2 border-green-400 dark:border-green-600">
              <span className="text-xl font-bold text-green-700 dark:text-green-300">
                +{xpEarned} XP
              </span>
            </div>
            <span className="text-3xl">✨</span>
          </div>
        )}

        {/* Message */}
        <p className="text-sm text-gray-600 dark:text-gray-400 mb-6">
          Mathematical Order grows stronger. Continue your training to seal
          more sources!
        </p>

        {/* Action Button */}
        <Button onClick={onClose} size="lg" className="w-full">
          Continue Training
        </Button>

        {/* Auto-dismiss hint */}
        <p className="text-xs text-gray-400 dark:text-gray-500 mt-4">
          Closes automatically in 5 seconds
        </p>
      </div>
    </div>
  );
}
